/**
 * generationFlow — orchestrator for the AI poster generator.
 *
 * Responsibilities:
 *   1. Debit credits (idempotent) before calling pse-pipeline-v3
 *   2. Submit to POST /api/v1/generations
 *   3. Poll GET /api/v1/generations/:jobId every POLL_INTERVAL_MS
 *   4. On terminal: if poster.status !== 'rejected', fetch image → save to library
 *   5. On rejection/failure: server has already refunded; just surface `creditsRefunded`
 *
 * Exposes a single `runGeneration()` async fn + an abort handle for cancel.
 *
 * WEB VARIANT of app/src/components/home/aiGenerator/generationFlow.js. Steps
 * 1-4 are byte-identical to the desktop file (keep them that way — the submit
 * body is a shipped-installer contract). Only step 5 differs: the desktop
 * fetches the image and hands base64 to Electron's SQLite `saveDesign`; the web
 * hands the cloud-library worker the URL and lets IT fetch server-side, because
 * R2 CORS-blocks a browser `fetch()` of the poster (an <img src> still works,
 * which is why the result screen and the print handoff use URLs throughout).
 */

import i18n from '../../../i18n';
// (imageHelpers stays available for the print handoff; the save path below
// never fetches pixels client-side.)

// 3 s since 2026-09-08 (latency plan Tier A5) — the average completion-to-
// reveal gap halves for a negligible extra load on the poll endpoint.
const POLL_INTERVAL_MS = 3000;
// 15 min hard cap. Pipeline worst-case (3 artist rounds with critic
// rejections) takes ~12-13 min on slow atlascloud days. The old 8 min cap
// surfaced a false "Generation timed out" on jobs that were still mid-
// artist-round-3 and would have completed within another 1-2 min (2026-05-15
// — job 99247026 hit the 8 min ceiling, finished successfully on the backend
// a couple minutes later).
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

function newIdempotencyKey(prefix) {
  const rand = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}_${rand}`;
}

/**
 * Execute a full generation. Calls `onPhase` with stage keys as the UI
 * progress advances: 'submitting' → 'polling' → 'saving' → 'done' | 'error'.
 *
 * @returns {Promise<{status:'done', poster, newBalance, creditsRefunded}
 *                  | {status:'rejected', creditsRefunded, errorMessage}
 *                  | {status:'insufficient', required, currentBalance}
 *                  | {status:'cancelled'}
 *                  | {status:'error', error}>}
 */
export async function runGeneration({
  prompt,
  subject,
  subjectCategoryId,
  gradeMin,
  gradeMax,
  styleId,
  // IIG Phase 4 — all optional and additive. When the standard picker was not
  // used, requestedStandardId/stampStandardCode/fetchAlignment are absent, so
  // the submit body and saved design are byte-identical to the legacy flow.
  requestedStandardId, // explicit curriculum-standard id the teacher picked
  stampStandardCode,   // footer-chip opt-in (boolean)
  intent,              // intent-first (2026-07-16): 'teach'|'motivate'|'classroom'|'school'|'custom' — additive, scopes the server matcher
  languageCode,        // content language (2026-07-22, LatAm expansion): BCP-47 from i18n; omitted when 'en' — poster text generated in this language
  viewerStateCode,     // teacher's USPS code — orients the result chip
  fetchAlignment,      // (posterId, stateCode) => Promise<alignment|null>
  wallet,            // { debit, canAfford, getCost, refreshBalance }
  generator,         // { submitGeneration, pollJob }
  saveDesign,        // web: useCloudLibrary().saveDesign({ name, source, imageUrl, metadata })
  onPhase,           // (phase: string) => void
  onPoll,            // (state: { pollCount, queueStatus, workflowStatus, posterStatus, errorMessage }) => void
  onPreview,         // ({ poster, prompt, jobId }) => void — fires the moment the backend is done, BEFORE the image download + library save (Tier A5, 2026-09-08)
  abortSignal,       // AbortSignal (optional)
}) {
  const log = (...args) => console.log('[aiGen]', ...args);
  const creditCost = wallet.getCost('ai_poster_generate');
  const creditIdempotencyKey = newIdempotencyKey('gen');
  const generationIdempotencyKey = newIdempotencyKey('job');
  log('start', { prompt: prompt?.slice(0, 60), subject, gradeMin, gradeMax, styleId, creditCost });

  // 1. Debit
  if (wallet.getCost('ai_poster_generate') != null && !wallet.canAfford('ai_poster_generate')) {
    log('insufficient (pre-flight)');
    return { status: 'insufficient', required: creditCost, currentBalance: null };
  }

  onPhase?.('submitting');
  const debitResult = await wallet.debit('ai_poster_generate', creditIdempotencyKey);
  log('debit result', debitResult);
  if (!debitResult?.success) {
    // Distinguish insufficient (402 shape) vs other errors
    const status = debitResult?.status ?? null;
    if (status === 402 || /insufficient/i.test(debitResult?.error || '')) {
      return {
        status: 'insufficient',
        required: debitResult?.data?.required ?? creditCost,
        currentBalance: debitResult?.data?.currentBalance ?? null,
      };
    }
    // Customer-facing copy only — the raw wallet error goes to the console.
    log('debit failed', debitResult?.error);
    return { status: 'error', error: i18n.t('home:generationFlow.debitFailed') };
  }

  // 2. Submit
  let submitResp;
  try {
    submitResp = await generator.submitGeneration({
      prompt,
      idempotencyKey: generationIdempotencyKey,
      subject: subject || undefined,
      subjectCategoryId: subjectCategoryId || undefined,
      gradeMin,
      gradeMax,
      styleId: styleId || undefined,
      // IIG Phase 4 — additive: undefined (omitted) unless the standard picker
      // was used, keeping the submit body identical to the legacy flow.
      requestedStandardId: requestedStandardId || undefined,
      stampStandardCode: stampStandardCode === true ? true : undefined,
      intent: intent || undefined,
      // Content language — additive: omitted for English so the body stays
      // byte-identical to the legacy flow (shipped-installer contract).
      languageCode: languageCode || undefined,
      creditIdempotencyKey,
      creditCost,
    });
    log('submit response', submitResp);
  } catch (err) {
    // Submit failed after debit — we're in a bad state. Surface it; the user
    // may need admin help. refreshBalance so any async refund is reflected.
    console.error('[aiGen] submit failed', err);
    await wallet.refreshBalance?.();
    // No raw err.message in the UI (production rule: no technical messages).
    log('submit failed', err.status || '', err.message);
    return { status: 'error', error: i18n.t('home:generationFlow.submitFailed') };
  }

  const jobId = submitResp?.jobId;
  if (!jobId) {
    await wallet.refreshBalance?.();
    return { status: 'error', error: i18n.t('home:generationFlow.noJobId') };
  }

  onPhase?.('polling');
  log('polling jobId', jobId);

  // 3. Poll
  const pollStart = Date.now();
  let terminalResponse = null;
  let pollCount = 0;
  let pollErrorCount = 0;
  while (true) {
    if (abortSignal?.aborted) {
      log('aborted during poll');
      return { status: 'cancelled' };
    }
    if (Date.now() - pollStart > POLL_TIMEOUT_MS) {
      log('poll timeout', { pollCount, pollErrorCount, elapsedMs: Date.now() - pollStart });
      // Tell the backend we gave up. Best-effort + fire-and-forget — the
      // backend flips the queue row to `cancelled` and the sweeper refunds
      // the credit on its next ~60 s tick. Without this signal the user is
      // charged for a poster they can't see (PrintLibrary cloud-sync isn't
      // built yet) — see 2026-05-15 fix.
      if (generator.abandonJob) {
        generator.abandonJob(jobId).catch(() => { /* already logged */ });
      }
      return { status: 'error', error: i18n.t('home:generationFlow.timedOut') };
    }

    let pollResp;
    try {
      pollResp = await generator.pollJob(jobId);
      pollCount++;
      onPoll?.({
        pollCount,
        queueStatus: pollResp?.queueStatus ?? null,
        workflowStatus: pollResp?.workflowStatus ?? null,
        posterStatus: pollResp?.poster?.status ?? null,
        errorMessage: pollResp?.errorMessage ?? null,
        // Real backend stage (2026-07-29 UI pass): the worker has always
        // returned `currentStage` (production_queue.current_stage) — surface
        // it so GenerationProgress can show stage truth instead of guessing
        // from a timer. Null on old workers / early polls → timer fallback.
        currentStage: pollResp?.currentStage ?? null,
      });
      if (pollCount <= 2 || pollCount % 5 === 0) {
        log(`poll #${pollCount}`, {
          queueStatus: pollResp?.queueStatus,
          workflowStatus: pollResp?.workflowStatus,
          posterStatus: pollResp?.poster?.status,
          errorMessage: pollResp?.errorMessage,
        });
      }
    } catch (err) {
      pollErrorCount++;
      console.warn(`[aiGen] poll #${pollCount + 1} error (${pollErrorCount} total)`, err.status || '', err.message);
      // Transient network blip — back off and retry, but not forever.
      await sleep(POLL_INTERVAL_MS);
      continue;
    }

    const queue = pollResp?.queueStatus;
    if (queue === 'completed' || queue === 'failed') {
      log('terminal', queue, { posterStatus: pollResp?.poster?.status, pollCount });
      terminalResponse = pollResp;
      break;
    }

    await sleep(POLL_INTERVAL_MS);
  }

  const posterStatus = terminalResponse.poster?.status;
  const creditsRefunded = terminalResponse.creditsRefunded ?? null;

  // 4. Rejection / failure → refund has already fired server-side
  if (terminalResponse.queueStatus === 'failed' || posterStatus === 'rejected') {
    log('rejected path', { queueStatus: terminalResponse.queueStatus, posterStatus, creditsRefunded, rawError: terminalResponse.errorMessage });
    await wallet.refreshBalance?.();
    return {
      status: 'rejected',
      creditsRefunded,
      // NEVER surface the backend's error_message — it carries pipeline
      // internals (critic scores, provider errors). Classify instead:
      // 'quality' = our quality bar rejected the artwork; 'technical' =
      // anything else. Raw text goes to the console for support/debugging.
      rejectionKind: (posterStatus === 'rejected' || /^Rejected:/.test(terminalResponse.errorMessage || ''))
        ? 'quality' : 'technical',
    };
  }

  // 5. Success → fetch image + save to library
  onPhase?.('saving');
  const poster = terminalResponse.poster;
  if (!poster) {
    log('success path but no poster in response', terminalResponse);
    return { status: 'error', error: i18n.t('home:generationFlow.noPoster') };
  }
  // Tier A5 (2026-09-08): reveal now. The result screen paints the CDN
  // full-res URL while the base64 download + thumbnail + saveDesign run
  // behind it; the screen's actions stay disabled until we return 'done'.
  try { onPreview?.({ poster, prompt, jobId }); } catch (e) { console.warn('[aiGen] onPreview threw (ignored)', e); }
  log('success poster', { id: poster.id, title: poster.title, status: poster.status, paths: {
    thumbnail_path: poster.thumbnail_path,
    preview_path: poster.preview_path,
    full_res_path: poster.full_res_path,
  } });

  const imageUrl = poster.full_res_path || poster.preview_path || poster.thumbnail_path;
  if (!imageUrl) {
    return { status: 'error', error: i18n.t('home:generationFlow.noImageUrl') };
  }

  try {
    // IIG Phase 4 — fetch the poster's standard alignment for the result chip
    // and the library-card stamp. Soft-fail: a chip-less save is fine when the
    // catalog worker is unreachable or the poster has no verified crosswalk.
    let alignment = null;
    try {
      alignment = (await fetchAlignment?.(poster.id, viewerStateCode)) ?? null;
    } catch { /* chip-less save is fine */ }

    log('saving to cloud library (URL-direct)', { imageUrl });
    const savedDesign = await saveDesign({
      name: poster.title || 'AI Poster',
      source: 'ai_generator',
      imageUrl,
      metadata: {
        source: 'aiPosterGenerator',
        platform: 'AI Generator',
        version: 2,
        jobId,
        posterId: poster.id,
        prompt,
        subject: subject || null,
        gradeMin: poster.gradeMin ?? gradeMin ?? null,
        gradeMax: poster.gradeMax ?? gradeMax ?? null,
        kimiConfidence: poster.kimiConfidence ?? null,
        // IIG Phase 4 — canonical alignment object (or null) + the linked
        // standard id (picker choice wins, then the resolved alignment).
        alignment,
        standardId: alignment?.standardId ?? requestedStandardId ?? null,
        // Content language (2026-07-22) — null for English/legacy designs.
        languageCode: languageCode || null,
        // Brightivity: shared row → the stored (StudioCore-readable) URLs, not the brightivity.dev display links.
        thumbnailUrl: (poster.stored ? poster.stored.thumbnail_path : poster.thumbnail_path) || null,
        previewUrl: (poster.stored ? poster.stored.preview_path : poster.preview_path) || null,
        fullResUrl: (poster.stored ? poster.stored.full_res_path : poster.full_res_path) || null,
      },
    });
    log('saved to library', { id: savedDesign?.id });

    try { await wallet.refreshBalance?.(); } catch (e) { console.warn('[aiGen] refreshBalance failed (non-fatal)', e); }
    onPhase?.('done');
    log('done');

    // Minimal `design` row in the shape ResultScreen + the print handoff read.
    // Desktop carries base64 (d_pdf / d_thumbnail); web carries URLs.
    const design = {
      id: savedDesign?.id ?? null,
      name: poster.title || 'AI Poster',
      imageUrl,
      source: 'ai_generator',
      d_metaData: { alignment },
    };

    return {
      status: 'done',
      poster,
      design,
      prompt,
      creditsRefunded,
      alignment,
    };
  } catch (err) {
    console.error('[aiGen] save-path exception', err);
    log('save threw', err.message);
    return { status: 'error', error: i18n.t('home:generationFlow.saveToLibraryFailed') };
  }
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
